import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Add, Edit, Delete, Circle, LocationOn } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { 
  addBlowerToSite, 
  getSiteWithInstruments, 
  updateBlowerInSite, 
  deleteBlowerFromSite,
  getTagNamesByProjectId,
  getTelemetryData,
  logActivity
} from '../../api/apiService';
import { TELEMETRY_LIVE_POLL_MS } from '../../constants/telemetry';

const BlowersTab = ({ projectId, siteData }) => {
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const isAdminOrManager = userRole === 'Admin' || userRole === 'Manager';

  // State for blowers data
  const [blowers, setBlowers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [telemetryData, setTelemetryData] = useState({});
  const [isLoadingTelemetry, setIsLoadingTelemetry] = useState(false);

  // State for tag names
  const [tagNames, setTagNames] = useState([]);
  const [loadingTagNames, setLoadingTagNames] = useState(false);

  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedBlower, setSelectedBlower] = useState(null);
  const [formData, setFormData] = useState({
    blowerDescription: '',
    blowerLocation: '',
    onOffTagName: '',
    tripTagName: ''
  });

  // Fetch blowers data when component mounts or projectId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const fullSiteData = await getSiteWithInstruments(projectId);
        setBlowers(fullSiteData.blowers || []);
      } catch (error) {
        console.error('Error fetching site data:', error);
        setBlowers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Fetch tag names from omsTelemetryLive table
  useEffect(() => {
    const fetchTagNames = async () => {
      if (!projectId) return;
      
      try {
        setLoadingTagNames(true);
        const names = await getTagNamesByProjectId(projectId);
        setTagNames(names);
      } catch (error) {
        console.error('Error fetching tag names:', error);
        setTagNames([]);
      } finally {
        setLoadingTagNames(false);
      }
    };

    fetchTagNames();
  }, [projectId]);

  // Fetch telemetry data for blowers
  useEffect(() => {
    const fetchTelemetryData = async () => {
      if (!projectId || !blowers.length) return;

      try {
        setIsLoadingTelemetry(true);
        const tagIds = [];
        
        // Collect all tag IDs from blowers
        blowers.forEach(blower => {
          if (blower.onOffTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DO_301
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${blower.onOffTagName}`;
            tagIds.push(formattedTagId);
          }
          if (blower.tripTagName) {
            // Format tagId from tagName: PRJ1255_PLC1:DI_401
            const serverName = siteData?.serverName || 'PLC1';
            const cleanServerName = serverName.replace(`${projectId}_`, '');
            const formattedTagId = `${projectId}_${cleanServerName}:${blower.tripTagName}`;
            tagIds.push(formattedTagId);
          }
        });

        const uniqueTagIds = [...new Set(tagIds)];
        if (uniqueTagIds.length === 0) return;

        console.log('[BlowersTab] Fetching telemetry for tagIds:', uniqueTagIds);
        const data = await getTelemetryData(projectId, uniqueTagIds);
        
        // Create a map of tagId to value
        const telemetryMap = {};
        data.forEach(item => {
          telemetryMap[item.tagId] = {
            value: item.value,
            timestamp: item.timestamp,
            localTime: item.localTime,
            deviceLocalTime: item.deviceLocalTime
          };
        });
        
        setTelemetryData(telemetryMap);
      } catch (error) {
        console.error('[BlowersTab] Error fetching telemetry data:', error);
      } finally {
        setIsLoadingTelemetry(false);
      }
    };

    // Initial fetch
    fetchTelemetryData();

    const intervalId = setInterval(fetchTelemetryData, TELEMETRY_LIVE_POLL_MS);

    return () => clearInterval(intervalId);
  }, [projectId, blowers, siteData]);

  // Handle form data change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    const newBlower = {
      id: Date.now(),
      blowerDescription: formData.blowerDescription,
      blowerLocation: formData.blowerLocation,
      onOffTagName: formData.onOffTagName,
      tripTagName: formData.tripTagName,
    };

    try {
      await addBlowerToSite(projectId, newBlower);
      console.log('New blower added successfully!');
      
      // Log add blower activity to DynamoDB
      try {
        await logActivity(currentUser.email, `Added blower: ${newBlower.blowerDescription}`);
      } catch (err) {
        console.error('Error logging activity to DynamoDB:', err);
      }
      
      const fullSiteData = await getSiteWithInstruments(projectId);
      setBlowers(fullSiteData.blowers || []);
      setOpenDialog(false);
      setFormData({
        blowerDescription: '',
        blowerLocation: '',
        onOffTagName: '',
        tripTagName: ''
      });
    } catch (error) {
      console.error('Error adding blower to DynamoDB:', error);
    }
  };

  // Handle Edit
  const handleEdit = (blower) => {
    setSelectedBlower(blower);
    setFormData({
      blowerDescription: blower.blowerDescription || '',
      blowerLocation: blower.blowerLocation || '',
      onOffTagName: blower.onOffTagName || '',
      tripTagName: blower.tripTagName || '',
    });
    setOpenEditDialog(true);
  };

  // Handle Delete
  const handleDelete = (blower) => {
    setSelectedBlower(blower);
    setOpenDeleteDialog(true);
  };

  // Handle Edit Submit
  const handleEditSubmit = async () => {
    try {
      const updatedBlower = {
        ...selectedBlower,
        ...formData,
      };
      
      // Ensure we're not creating a new ID
      if (!updatedBlower.id) {
        updatedBlower.id = selectedBlower.id;
      }

      if (userRole === 'Admin' || userRole === 'Manager') {
        await updateBlowerInSite(projectId, updatedBlower);
        // Log edit blower activity
        try {
          await logActivity(currentUser.email, `Edited blower: ${updatedBlower.blowerDescription}`);
        } catch (err) {
          console.error('Error logging edit blower activity to DynamoDB:', err);
        }
      }
      // Update local state for all roles
      const fullSiteData = await getSiteWithInstruments(projectId);
      setBlowers(fullSiteData.blowers || []);
      
      // Close dialog and reset state
      setOpenEditDialog(false);
      setSelectedBlower(null);
      setFormData({
        blowerDescription: '',
        blowerLocation: '',
        onOffTagName: '',
        tripTagName: ''
      });
    } catch (error) {
      console.error('Error updating blower:', error);
    }
  };

  // Handle Delete Confirm
  const handleDeleteConfirm = async () => {
    try {
      await deleteBlowerFromSite(projectId, selectedBlower.id);
      // Log delete blower activity
      try {
        await logActivity(currentUser.email, `Deleted blower: ${selectedBlower.blowerDescription}`);
      } catch (err) {
        console.error('Error logging delete blower activity to DynamoDB:', err);
      }
      console.log('Blower deleted successfully!');
      const fullSiteData = await getSiteWithInstruments(projectId);
      setBlowers(fullSiteData.blowers || []);
      setOpenDeleteDialog(false);
      setSelectedBlower(null);
    } catch (error) {
      console.error('Error deleting blower:', error);
    }
  };

  // Get blower status based on telemetry data
  const getBlowerStatus = (blower) => {
    if (!blower.onOffTagName) {
      return 'Unknown';
    }
    
    // Format tagId from tagName for telemetry lookup
    const serverName = siteData?.serverName || 'PLC1';
    const cleanServerName = serverName.replace(`${projectId}_`, '');
    const onOffTagId = `${projectId}_${cleanServerName}:${blower.onOffTagName}`;
    
    if (!telemetryData[onOffTagId]) {
      return 'Unknown';
    }
    
    const onOffValue = telemetryData[onOffTagId].value;
    
    // Check for trip condition first
    if (blower.tripTagName) {
      const tripTagId = `${projectId}_${cleanServerName}:${blower.tripTagName}`;
      if (telemetryData[tripTagId]) {
        const tripValue = telemetryData[tripTagId].value;
        if (tripValue === '1' || tripValue === 1 || tripValue === 'true' || tripValue === true) {
          return 'Trip';
        }
      }
    }
    
    // Check on/off status
    if (onOffValue === '1' || onOffValue === 1 || onOffValue === 'true' || onOffValue === true) {
      return 'On';
    } else if (onOffValue === '0' || onOffValue === 0 || onOffValue === 'false' || onOffValue === false) {
      return 'Off';
    }
    
    return 'Unknown';
  };

  // Get status indicator component
  const getStatusIndicator = (status) => {
    const isOn = status === 'On';
    const isOff = status === 'Off';
    const isTrip = status === 'Trip';
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* On Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOn ? theme.palette.status.normalGreen : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOn ? theme.palette.status.normalGreen : theme.palette.grey[500], 
            fontWeight: isOn ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            ON
          </Typography>
        </Box>
        
        {/* Off Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isOff ? theme.palette.status.alarmRed : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isOff ? theme.palette.status.alarmRed : theme.palette.grey[500], 
            fontWeight: isOff ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            OFF
          </Typography>
        </Box>
        
        {/* Trip Indicator */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1 
        }}>
          <Circle sx={(theme) => ({ 
            color: isTrip ? theme.palette.status.warningAmber : theme.palette.grey[300], 
            fontSize: 24,
            transition: 'color 0.3s ease'
          })} />
          <Typography variant="caption" sx={(theme) => ({ 
            color: isTrip ? theme.palette.status.warningAmber : theme.palette.grey[500], 
            fontWeight: isTrip ? 600 : 400,
            fontSize: '0.75rem',
            transition: 'all 0.3s ease'
          })}>
            TRIP
          </Typography>
        </Box>
      </Box>
    );
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: (theme) => theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: { xs: '300px', sm: '350px', md: '400px' },
      overflow: 'auto',
      position: 'relative'
    }}>
      <Stack spacing={2}>
        {blowers.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px',
            color: (theme) => theme.palette.text.secondary
          }}>
            <Typography variant="body1">No blowers found. Add your first blower using the + button.</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
            {blowers.map((blower, index) => {
              const status = getBlowerStatus(blower);
              
              return (
                <Card key={blower.id || index} sx={{ 
                  borderRadius: '16px', 
                  boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 12px rgba(0,0,0,0.06)',
                  backgroundColor: (theme) => theme.palette.background.paper,
                  border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 10px 28px rgba(0,0,0,0.45)' : '0 8px 24px rgba(0,0,0,0.10)',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.10)',
                  }
                }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Typography variant="h6" sx={{ 
                        fontWeight: 700, 
                        fontSize: '1.1rem',
                        color: (theme) => theme.palette.text.primary,
                        lineHeight: 1.3
                      }}>
                        {blower.blowerDescription}
                      </Typography>
                      {isAdminOrManager && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Blower">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(blower);
                              }}
                              sx={{
                                color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Blower">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(blower);
                              }}
                              sx={{
                                color: (theme) => theme.palette.error.main,
                                '&:hover': { backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) },
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                    <Stack spacing={2}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        p: 1.5,
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.10 : 0.04),
                        borderRadius: '8px'
                      }}>
                        <Typography variant="body2" sx={{ 
                          color: (theme) => theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <LocationOn sx={{ fontSize: 16, mr: 0.5, color: (theme) => theme.palette.text.secondary }} /> {blower.blowerLocation}
                        </Typography>
                      </Box>
                      
                      {/* Status Indicators */}
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        p: 2,
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                        borderRadius: '8px'
                      }}>
                        {isLoadingTelemetry ? (
                          <CircularProgress size={20} sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main }} />
                        ) : (
                          getStatusIndicator(status)
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Stack>
      
      {isAdminOrManager && (
        <Fab
          color="primary"
          aria-label="add blower"
          onClick={() => setOpenDialog(true)}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
            backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            color: (theme) => theme.palette.getContrastText(theme.palette.brand?.royalBlue || theme.palette.primary.main),
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                : theme.palette.brand?.royalBlue || theme.palette.primary.main,
              filter: (theme) => theme.palette.mode === 'dark' ? 'none' : 'brightness(0.95)',
            },
          }}
        >
          <Add />
        </Fab>
      )}

      {/* Add Blower Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: "12px", 
            padding: {
              xs: "12px",
              sm: "16px",
              md: "20px"
            },
            boxShadow: "0px 8px 24px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: "600", 
          color: "#1A1F36",
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: {
            xs: '1.1rem',
            sm: '1.25rem',
            md: '1.5rem'
          }
        }}>
          Add New Blower
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: "16px" }}>
            <TextField
              fullWidth
              label="Blower Description"
              name="blowerDescription"
              value={formData.blowerDescription}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <TextField
              fullWidth
              label="Blower Location"
              name="blowerLocation"
              value={formData.blowerLocation}
              onChange={handleFormChange}
              variant="outlined"
              required
            />
            <FormControl fullWidth>
              <InputLabel id="on-off-tag-label">On/Off Tag Name</InputLabel>
              <Select
                labelId="on-off-tag-label"
                label="On/Off Tag Name"
                name="onOffTagName"
                value={formData.onOffTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
                required
              >
                <MenuItem value="">Select an On/Off Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="trip-tag-label">Trip Tag Name</InputLabel>
              <Select
                labelId="trip-tag-label"
                label="Trip Tag Name"
                name="tripTagName"
                value={formData.tripTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Trip Tag Name (Optional)</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: "16px" }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            color="secondary" 
            sx={{ 
              textTransform: "none",
              color: '#666',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            sx={{ 
              textTransform: "none", 
              boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Blower Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Edit Blower</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: '16px' }}>
            <TextField
              fullWidth
              label="Blower Description"
              name="blowerDescription"
              value={formData.blowerDescription}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Blower Location"
              name="blowerLocation"
              value={formData.blowerLocation}
              onChange={handleFormChange}
              variant="outlined"
            />
            <FormControl fullWidth>
              <InputLabel id="edit-on-off-tag-label">On/Off Tag Name</InputLabel>
              <Select
                labelId="edit-on-off-tag-label"
                label="On/Off Tag Name"
                name="onOffTagName"
                value={formData.onOffTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select an On/Off Tag Name</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="edit-trip-tag-label">Trip Tag Name</InputLabel>
              <Select
                labelId="edit-trip-tag-label"
                label="Trip Tag Name"
                name="tripTagName"
                value={formData.tripTagName}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTagNames}
              >
                <MenuItem value="">Select a Trip Tag Name (Optional)</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
          >
            Submit
          </Button>
          <Button 
            onClick={() => setOpenEditDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Blower Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Delete Blower</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this blower?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="primary"
          >
            Delete
          </Button>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlowersTab; 